import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../api/client';
import * as pa from '../api/pa';
import type { PASubscriber } from '../api/pa';

/**
 * THE SESSION — bootstrap, sign-in, sign-out, and the identity every screen reads.
 *
 * # The bootstrap is the whole point of this file
 *
 * The SPA cannot read its own session: `billing_pa_session` is HttpOnly, so nothing in
 * the page can tell whether a session exists. The ONLY way to know is to ask
 * `GET /pa/auth/me` and interpret the answer — which is also what makes the server's
 * live re-read effective, since an archived card loses its session at that call.
 *
 * ⚠ THE FOUR STATES ARE EXHAUSTIVE ON PURPOSE, and the third is why:
 *
 *   loading       — the bootstrap call is in flight, and NOTHING ELSE renders
 *   anonymous     — /me answered 401: show the login screen. A 401 is a normal answer
 *                   here (the idle TTL slid past), not an error, so it raises no alert.
 *   unavailable   — /me failed for any OTHER reason (offline, 503 `pa.not_configured`,
 *                   a proxy answering 502). This state exists so the portal can never
 *                   show a spinner that resolves to nothing: the subscriber is told what
 *                   happened and given «Повторить».
 *   authenticated — the identity, plus the expiry the header shows.
 *
 * A build that collapsed `unavailable` into `anonymous` would put a login form in front
 * of a subscriber whose credentials cannot possibly work, and he would conclude his
 * password was wrong and phone the operator. That is the failure this state prevents.
 *
 * # Nothing is stored
 *
 * No token, no identity, no "remember me" — the cookie is the session and the browser
 * owns it. `src/storage.test.ts` fails if any module in this app names `localStorage`,
 * `sessionStorage`, `indexedDB`, `caches` or `document.cookie`.
 */

export type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'unavailable'; error: unknown }
  | { status: 'authenticated'; subscriber: PASubscriber; expiresAt: string };

export interface SessionApi {
  state: SessionState;
  /** Signs in and resolves; REJECTS with the ApiError so the form can show it inline. */
  signIn: (login: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads /me — used after a password change clears `must_change_password`. */
  refresh: () => Promise<void>;
  /** Retries the bootstrap from the `unavailable` state. */
  retry: () => void;
}

const SessionContext = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  // The bootstrap. It runs once per mount and once per `retry()`, and it is the only
  // place `loading` is entered — every later transition comes from an explicit action,
  // so no screen can be replaced by a spinner underneath the subscriber's hands.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const principal = await pa.me();
        if (cancelled) return;
        setState({
          status: 'authenticated',
          subscriber: principal.subscriber,
          expiresAt: principal.expires_at,
        });
      } catch (err) {
        if (cancelled) return;
        // 401 is "no session", which is the ordinary first visit — not a failure.
        if (err instanceof ApiError && err.status === 401) {
          setState({ status: 'anonymous' });
          return;
        }
        setState({ status: 'unavailable', error: err });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const signIn = useCallback(async (login: string, password: string) => {
    // No optimistic transition: the state moves only when the server has set the
    // cookie, so a refused login leaves the form exactly where it was.
    const session = await pa.login(login, password);
    setState({
      status: 'authenticated',
      subscriber: session.subscriber,
      expiresAt: session.expires_at,
    });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await pa.logout();
    } catch {
      // ⚠ SWALLOWED ON PURPOSE, and it must be a `catch` rather than a `finally`.
      //
      // A `try { await } finally { … }` runs the cleanup and then RE-THROWS, so the
      // rejection reaches the caller — which in `PortalShell` is
      // `void signOut().finally(…)` and therefore an UNHANDLED PROMISE REJECTION on every
      // failed logout. That is how this was first written, and the suite's exit code (not
      // its test list: 53 passed, exit 1) is what found it.
      //
      // There is nothing for the caller to do with the error anyway: the route is
      // idempotent, its failure modes are network ones, and the subscriber's intent —
      // «stop showing my account on this phone» — is satisfied locally. What the server
      // did not manage to clear is bounded by the session's idle TTL.
    }
    // ALWAYS ANONYMOUS AFTERWARDS, whatever the call did.
    setState({ status: 'anonymous' });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const principal = await pa.me();
      setState({
        status: 'authenticated',
        subscriber: principal.subscriber,
        expiresAt: principal.expires_at,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous' });
        return;
      }
      // A failed refresh must not log a working session out: keep what we have.
    }
  }, []);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const value = useMemo<SessionApi>(
    () => ({ state, signIn, signOut, refresh, retry }),
    [state, signIn, signOut, refresh, retry],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
